import { ThemeEvents } from '@theme/events';

const CUSTOM_SIZE = 'Custom Size';

class DeliveryEstimate extends HTMLElement {
  #controller = new AbortController();
  #sizeObserver;
  #customSizeSelected = false;

  connectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const section = this.closest('.shopify-section, dialog');

    this.addEventListener('change', this.#onShippingChange, { signal });
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.#observeCustomSize(section);
    this.#render();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#sizeObserver?.disconnect();
  }

  #observeCustomSize(section) {
    this.#sizeObserver?.disconnect();

    const sizePicker = section?.querySelector('cs-size-dress');
    if (!sizePicker) {
      this.#setCustomSize(false);
      return;
    }

    const sync = () => {
      const selectedSize = [...sizePicker.querySelectorAll('[data-cs-size-text]')]
        .map((element) => element.textContent?.trim())
        .find(Boolean);
      this.#setCustomSize(selectedSize === CUSTOM_SIZE);
    };

    sync();
    this.#sizeObserver = new MutationObserver(sync);
    this.#sizeObserver.observe(sizePicker, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  #setCustomSize(selected) {
    if (selected === this.#customSizeSelected) return;
    this.#customSizeSelected = selected;
    this.#render();
  }

  #onShippingChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-shipping-min]')) {
      this.#render();
    }
  };

  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const nextEstimate = [...event.detail.data.html.querySelectorAll('delivery-estimate')].find(
      (estimate) => estimate.dataset.blockId === this.dataset.blockId
    );

    if (nextEstimate instanceof HTMLElement) {
      this.dataset.productId = nextEstimate.dataset.productId || this.dataset.productId;
      this.dataset.fastShip = nextEstimate.dataset.fastShip || 'false';
      this.dataset.customColor = nextEstimate.dataset.customColor || 'false';
      this.dataset.variantAvailable = nextEstimate.dataset.variantAvailable || 'false';
      this.hidden = this.dataset.variantAvailable !== 'true';
    }

    queueMicrotask(() => {
      this.#observeCustomSize(this.closest('.shopify-section, dialog'));
      this.#render();
    });
  };

  #render() {
    if (this.dataset.variantAvailable !== 'true') {
      this.hidden = true;
      return;
    }

    const shippingOption = this.querySelector('input[data-shipping-min]:checked');
    const range = this.querySelector('[data-delivery-range]');
    const productionLabel = this.querySelector('[data-production-label]');
    const productionIcon = this.querySelector('[data-production-icon]');
    const production = this.querySelector('[data-production-text]');
    const shippingTime = this.querySelector('[data-shipping-time]');
    const selectedShippingMethod = this.querySelector('[data-selected-shipping-method]');
    if (
      !(shippingOption instanceof HTMLInputElement) ||
      !range ||
      !(productionLabel instanceof HTMLElement) ||
      !(productionIcon instanceof HTMLElement) ||
      !production ||
      !shippingTime ||
      !selectedShippingMethod
    ) {
      this.hidden = true;
      return;
    }

    const fastDays = this.#number(this.dataset.fastProductionDays);
    const productionMin = this.#number(this.dataset.regularProductionMin);
    const productionMax = this.#number(this.dataset.regularProductionMax);
    const shippingMin = this.#number(shippingOption.dataset.shippingMin);
    const shippingMax = this.#number(shippingOption.dataset.shippingMax);
    const start = this.#getOrderStartDate();
    if (
      !(start instanceof Date) ||
      [fastDays, productionMin, productionMax, shippingMin, shippingMax].some((value) => value === null)
    ) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    const fastEligible =
      this.dataset.fastShip === 'true' && this.dataset.customColor !== 'true' && !this.#customSizeSelected;

    let productionMinDate;
    let productionMaxDate;
    if (fastEligible) {
      productionMinDate = this.#addBusinessDays(start, fastDays);
      productionMaxDate = productionMinDate;
      productionLabel.hidden = true;
      productionIcon.hidden = false;
      production.textContent = `Ships in ${fastDays * 24} hours`;
    } else {
      productionMinDate = this.#addCalendarDays(start, Math.min(productionMin, productionMax));
      productionMaxDate = this.#addCalendarDays(start, Math.max(productionMin, productionMax));
      productionLabel.hidden = false;
      productionIcon.hidden = true;
      production.textContent = this.#formatProductionTime(productionMin, productionMax);
    }

    const normalizedShippingMin = Math.min(shippingMin, shippingMax);
    const normalizedShippingMax = Math.max(shippingMin, shippingMax);
    shippingTime.textContent = `${normalizedShippingMin} - ${normalizedShippingMax} business days`;
    selectedShippingMethod.textContent = shippingOption.dataset.shippingLabel;

    const earliest = this.#addBusinessDays(productionMinDate, normalizedShippingMin);
    const latest = this.#addBusinessDays(productionMaxDate, normalizedShippingMax);

    range.textContent = `${this.#formatDate(earliest)} - ${this.#formatDate(latest)}`;
    range.setAttribute('aria-busy', 'false');
  }

  #getOrderStartDate() {
    const now = new Date();
    const timeZone = this.dataset.timeZone;
    const cutoff = this.#number(this.dataset.cutoffHour);
    if (!timeZone || cutoff === null) return null;

    let dateParts;

    try {
      dateParts = this.#getZonedParts(now, timeZone);
    } catch (_error) {
      return null;
    }

    if (![dateParts.year, dateParts.month, dateParts.day, dateParts.hour].every(Number.isFinite)) return null;

    let start = new Date(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day, 12));
    if (dateParts.hour >= cutoff) start = this.#addCalendarDays(start, 1);

    while (this.#isWeekend(start)) start = this.#addCalendarDays(start, 1);
    return start;
  }

  #getZonedParts(date, timeZone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      hourCycle: 'h23',
    });
    const values = {};
    for (const part of formatter.formatToParts(date)) {
      if (part.type !== 'literal') values[part.type] = Number(part.value);
    }
    return values;
  }

  #addCalendarDays(date, days) {
    const result = new Date(date);
    result.setUTCDate(result.getUTCDate() + days);
    return result;
  }

  #addBusinessDays(date, days) {
    let result = new Date(date);
    let added = 0;
    while (added < days) {
      result = this.#addCalendarDays(result, 1);
      if (!this.#isWeekend(result)) added += 1;
    }
    return result;
  }

  #isWeekend(date) {
    const day = date.getUTCDay();
    return day === 0 || day === 6;
  }

  #formatDate(date) {
    const locale = document.documentElement.lang || 'en-US';
    const formatter = new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });

    if (!locale.toLowerCase().startsWith('en')) return formatter.format(date);

    const parts = Object.fromEntries(
      formatter
        .formatToParts(date)
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value])
    );
    return `${(parts.month || '').replace(/\.$/, '')}. ${parts.day || ''}`;
  }

  #formatProductionTime(minimum, maximum) {
    const min = Math.min(minimum, maximum);
    const max = Math.max(minimum, maximum);
    if (min % 7 === 0 && max % 7 === 0) {
      const minWeeks = min / 7;
      const maxWeeks = max / 7;
      if (minWeeks === maxWeeks) return `${minWeeks} ${minWeeks === 1 ? 'week' : 'weeks'}`;
      return `${minWeeks} - ${maxWeeks} weeks`;
    }
    if (min === max) return `${min} ${min === 1 ? 'day' : 'days'}`;
    return `${min} - ${max} days`;
  }

  #number(value) {
    if (value === undefined || value === null || value.trim() === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }
}

if (!customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', DeliveryEstimate);
}
